// Slice 7: per-plant-row "More actions" dropdown that consolidates
// Edit / Set Default / Deactivate / Delete into a single button so
// the plant row can keep its primary CTA real estate for the
// dealers-attached affordances (Attach existing dealer / Add non-app
// location).
//
// Lightweight on purpose: a single button + popover anchored
// right-aligned. Click anywhere outside closes; selecting an item
// closes + calls the parent callback. No headless-ui dep — we'd
// otherwise pull a 50KB library for one dropdown.
import { useEffect, useRef, useState } from "react";
import type { MfgPlant } from "@/lib/manufacturingClient";

const menuItemBase =
  "block w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50 dark:hover:bg-zinc-800 disabled:cursor-not-allowed disabled:opacity-50";
const dangerItem =
  "text-red-700 hover:bg-red-50 dark:text-red-300 dark:hover:bg-red-950/30";

export function PlantRowActionsMenu({
  plant,
  busy,
  onEdit,
  onSetDefault,
  onToggleActive,
  onDelete,
}: {
  plant: MfgPlant;
  busy: boolean;
  onEdit: (p: MfgPlant) => void;
  onSetDefault: (p: MfgPlant) => void;
  onToggleActive: (p: MfgPlant) => void;
  onDelete: (p: MfgPlant) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    }
    function onEsc(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  return (
    <div className="relative" ref={ref} onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        className="rounded-lg border border-gray-300 bg-white px-2 py-1 text-xs text-gray-800 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-100 dark:hover:bg-zinc-700"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        title="More plant actions"
      >
        More ▾
      </button>
      {open ? (
        <div
          role="menu"
          className="absolute right-0 z-20 mt-1 w-44 rounded-lg border border-gray-200 bg-white py-1 shadow-lg dark:border-zinc-700 dark:bg-zinc-900"
        >
          <button
            type="button"
            role="menuitem"
            className={menuItemBase}
            onClick={() => {
              setOpen(false);
              onEdit(plant);
            }}
            disabled={busy}
          >
            Edit
          </button>
          {!plant.is_default ? (
            <button
              type="button"
              role="menuitem"
              className={menuItemBase}
              onClick={() => {
                setOpen(false);
                onSetDefault(plant);
              }}
              disabled={busy}
            >
              Set default
            </button>
          ) : null}
          {!plant.is_default ? (
            <button
              type="button"
              role="menuitem"
              className={menuItemBase}
              onClick={() => {
                setOpen(false);
                onToggleActive(plant);
              }}
              disabled={busy}
            >
              {plant.is_active ? "Deactivate" : "Reactivate"}
            </button>
          ) : null}
          {!plant.is_default ? (
            <button
              type="button"
              role="menuitem"
              className={`${menuItemBase} ${dangerItem}`}
              onClick={() => {
                setOpen(false);
                onDelete(plant);
              }}
              disabled={busy}
              title="Hard-delete this plant (refused if anything still references it)"
            >
              Delete…
            </button>
          ) : null}
          {plant.is_default ? (
            <div className="px-3 py-1.5 text-xs text-gray-500 dark:text-zinc-400">
              Default plant — promote another first.
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
